/**
 * Cognitive Scoring & Profile Generator for Kazakan Cognitive Test
 * Implements domain-specific response time baselines, difficulty scaling,
 * anti-guessing biological floors, and cognitive fluency efficiency bonuses.
 * Strictly adheres to non-diagnostic, exploratory reporting principles.
 */

import { DOMAINS } from './question-bank.js';
import { renderRadarChart } from './svg-builder.js';

/**
 * Domain-specific response time baselines (in seconds)
 * Calibrated against Cattell-Horn-Carroll (CHC) cognitive load principles
 */
export const DOMAIN_TIME_PROFILES = {
  processing_speed: {
    floorSec: 1.2,
    optimalSec: 6.5,
    nominalSec: 12.0
  },
  working_memory: {
    floorSec: 1.8,
    optimalSec: 12.0,
    nominalSec: 20.0
  },
  abstract_reasoning: {
    floorSec: 2.0,
    optimalSec: 18.0,
    nominalSec: 32.0
  },
  spatial_reasoning: {
    floorSec: 2.0,
    optimalSec: 20.0,
    nominalSec: 34.0
  },
  quantitative_reasoning: {
    floorSec: 2.2,
    optimalSec: 22.0,
    nominalSec: 38.0
  },
  verbal_reasoning: {
    floorSec: 3.5,
    optimalSec: 22.0,
    nominalSec: 36.0
  },
  practical_social: {
    floorSec: 3.5,
    optimalSec: 24.0,
    nominalSec: 40.0
  }
};

const DEFAULT_TIME_PROFILE = {
  floorSec: 2.0,
  optimalSec: 18.0,
  nominalSec: 32.0
};

/**
 * Difficulty-specific weight and time scaling multipliers
 */
export const DIFFICULTY_PROFILES = {
  1: { baseWeight: 1.0, timeMultiplier: 0.85 },
  2: { baseWeight: 1.3, timeMultiplier: 1.00 },
  3: { baseWeight: 1.7, timeMultiplier: 1.35 }
};

export const MAX_SPEED_BONUS = 0.15; // Up to 15% bonus for peak cognitive automaticity

/**
 * Calculates psychometrically calibrated points and fluency bonus for a single item response
 *
 * @param {string} domainId - Cognitive domain ID
 * @param {number} difficulty - 1 (Easy), 2 (Medium), 3 (Hard)
 * @param {number} timeMs - Elapsed response time in milliseconds
 * @param {boolean} isCorrect - Whether the response is correct
 * @returns {object} { earnedPoints, baseWeight, maxPossible, bonusMultiplier, bonusPct }
 */
export function calculateItemScore(domainId, difficulty = 2, timeMs = 0, isCorrect = false) {
  const diffProfile = DIFFICULTY_PROFILES[difficulty] || DIFFICULTY_PROFILES[2];
  const domainProfile = DOMAIN_TIME_PROFILES[domainId] || DEFAULT_TIME_PROFILE;

  const baseWeight = diffProfile.baseWeight;
  const maxPossible = baseWeight * (1 + MAX_SPEED_BONUS);

  if (!isCorrect) {
    return {
      earnedPoints: 0,
      baseWeight,
      maxPossible,
      bonusMultiplier: 0,
      bonusPct: 0
    };
  }

  const timeSec = Math.max(0, timeMs / 1000);
  const floor = domainProfile.floorSec;
  const optimal = domainProfile.optimalSec * diffProfile.timeMultiplier;
  const nominal = domainProfile.nominalSec * diffProfile.timeMultiplier;

  let bonusMultiplier = 0;

  if (timeSec < floor) {
    // Under biological floor: anti-guessing guardrail (0 bonus, only base points)
    bonusMultiplier = 0;
  } else if (timeSec <= optimal) {
    // High cognitive automaticity: full speed bonus
    bonusMultiplier = MAX_SPEED_BONUS;
  } else if (timeSec < nominal) {
    // Linear scale down from full bonus to 0
    const ratio = (nominal - timeSec) / (nominal - optimal);
    bonusMultiplier = MAX_SPEED_BONUS * Math.max(0, Math.min(1, ratio));
  } else {
    // Deliberate contemplation: full base points, 0 bonus (no penalty)
    bonusMultiplier = 0;
  }

  const earnedPoints = baseWeight * (1 + bonusMultiplier);

  return {
    earnedPoints,
    baseWeight,
    maxPossible,
    bonusMultiplier,
    bonusPct: Math.round(bonusMultiplier * 1000) / 10
  };
}

/**
 * Computes comprehensive session scores, psychometric performance index,
 * domain breakdown, and personalized profile
 *
 * @param {Array} records - Array of session response records
 * @param {number} totalTarget - Total target question count (default: 28)
 * @param {string} lang - 'ar' or 'en'
 * @returns {object} Structured session scores and visual assets
 */
export function computeSessionScores(records, totalTarget = 28, lang = 'ar') {
  const answeredCount = records.length;
  const correctCount = records.filter(r => r.isCorrect).length;
  const unreachedCount = Math.max(0, totalTarget - answeredCount);

  // Response times analysis
  const times = records.map(r => r.timeMs).filter(t => t > 0).sort((a, b) => a - b);
  const medianTimeMs = times.length > 0 ? times[Math.floor(times.length / 2)] : 0;
  const avgTimeSec = times.length > 0 ? (times.reduce((a, b) => a + b, 0) / times.length / 1000).toFixed(1) : 0;

  // Domain breakdown initialization
  const domainStats = {};
  DOMAINS.forEach(d => {
    domainStats[d.id] = {
      id: d.id,
      name: lang === 'ar' ? d.name_ar : d.name_en,
      color: d.color,
      total: 0,
      correct: 0,
      sumTimeMs: 0,
      times: [],
      baseEarned: 0,
      basePossible: 0,
      totalEarned: 0,
      totalPossible: 0,
      fluencyBonusPoints: 0,
      scorePct: 0,
      bonusPct: 0,
      medianTimeMs: 0,
      avgTimeSec: 0
    };
  });

  // Calculate weighted points & cognitive fluency bonuses
  let totalEarnedPoints = 0;
  let totalMaxPossiblePoints = 0;
  let totalBaseEarnedPoints = 0;
  let totalBasePossiblePoints = 0;

  records.forEach(r => {
    const dId = r.question ? r.question.domainId : 'abstract_reasoning';
    const diff = r.question ? (r.question.difficulty || 2) : 2;
    const timeMs = r.timeMs || 0;
    const isCorrect = Boolean(r.isCorrect);

    const itemScore = calculateItemScore(dId, diff, timeMs, isCorrect);

    totalEarnedPoints += itemScore.earnedPoints;
    totalMaxPossiblePoints += itemScore.maxPossible;
    totalBaseEarnedPoints += isCorrect ? itemScore.baseWeight : 0;
    totalBasePossiblePoints += itemScore.baseWeight;

    const ds = domainStats[dId];
    if (ds) {
      ds.total++;
      ds.times.push(timeMs);
      ds.sumTimeMs += timeMs;
      ds.basePossible += itemScore.baseWeight;
      ds.totalPossible += itemScore.maxPossible;
      if (isCorrect) {
        ds.correct++;
        ds.baseEarned += itemScore.baseWeight;
        ds.totalEarned += itemScore.earnedPoints;
        ds.fluencyBonusPoints += (itemScore.earnedPoints - itemScore.baseWeight);
      }
    }
  });

  // Post-process domain metrics
  DOMAINS.forEach(d => {
    const ds = domainStats[d.id];
    if (ds.total > 0) {
      ds.avgTimeSec = Number((ds.sumTimeMs / ds.total / 1000).toFixed(1));
      ds.times.sort((a, b) => a - b);
      ds.medianTimeMs = ds.times[Math.floor(ds.times.length / 2)];
      ds.scorePct = ds.totalPossible > 0 ? Math.min(100, Math.round((ds.totalEarned / ds.totalPossible) * 100)) : 0;
      ds.bonusPct = ds.basePossible > 0 ? Math.round((ds.fluencyBonusPoints / ds.basePossible) * 1000) / 10 : 0;
    }
  });

  const accuracyPct = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;
  const performanceIndex = totalMaxPossiblePoints > 0 ? Math.min(100, Math.round((totalEarnedPoints / totalMaxPossiblePoints) * 100)) : 0;

  // Session-level fluency bonus percentage
  const totalFluencyBonusPoints = totalEarnedPoints - totalBaseEarnedPoints;
  const fluencyBonusPct = totalBasePossiblePoints > 0
    ? Math.round((totalFluencyBonusPoints / totalBasePossiblePoints) * 1000) / 10
    : 0;

  // Response speed categorization
  let speedCategory_ar = 'متزن ومدروس';
  let speedCategory_en = 'Deliberate & Thoughtful';
  if (medianTimeMs < 16000) {
    speedCategory_ar = 'استجابة فائقة السرعة';
    speedCategory_en = 'Rapid & Immediate';
  } else if (medianTimeMs < 28000) {
    speedCategory_ar = 'متوسط ومعتدل';
    speedCategory_en = 'Moderate Pace';
  }

  // Identify Strengths & Growth Areas based on domain proficiency
  const sortedDomains = Object.values(domainStats).sort((a, b) => {
    return (b.scorePct || 0) - (a.scorePct || 0);
  });

  const strengths = sortedDomains.slice(0, 2).filter(d => d.total > 0 && d.correct >= 2);
  const growthAreas = sortedDomains.slice(-2).filter(d => d.total > 0 && (d.scorePct < 75));

  // Radar chart data using normalized domain score percentages
  const radarChartData = DOMAINS.map(d => {
    const stats = domainStats[d.id];
    return {
      name: lang === 'ar' ? d.name_ar.split(' ')[0] : d.name_en.split(' ')[0],
      score: stats.scorePct,
      max: 100
    };
  });

  const radarSvg = renderRadarChart(radarChartData, 280);

  // Build copyable summary text
  const isAr = lang === 'ar';
  const summaryText = isAr ?
`📊 ملخص اختبار القدرات المعرفية — Kazakan Tools
• مؤشر الأداء الاستكشافي: ${performanceIndex} / 100
• دقة الإجابات: ${correctCount} من أصل ${answeredCount} (${accuracyPct}%)
• بونص الطلاقة والسرعة: +${fluencyBonusPct}%
• وسيط زمن الاستجابة: ${(medianTimeMs / 1000).toFixed(1)} ثانية (${speedCategory_ar})
• المجالات الأبرز: ${strengths.map(s => s.name).join(' ، ') || 'أداء متوازن'}
ملاحظة: هذا الاختبار أداة استكشافية للتطوير الذاتي وليست مقياسًا طبيًا معياريًا.` :
`📊 Cognitive Assessment Summary — Kazakan Tools
• Exploratory Performance Index: ${performanceIndex} / 100
• Accuracy: ${correctCount} of ${answeredCount} (${accuracyPct}%)
• Cognitive Fluency Bonus: +${fluencyBonusPct}%
• Median Response Time: ${(medianTimeMs / 1000).toFixed(1)}s (${speedCategory_en})
• Top Domains: ${strengths.map(s => s.name).join(', ') || 'Balanced Performance'}
Note: This test is an exploratory self-discovery tool, not a clinical diagnostic IQ measure.`;

  return {
    answeredCount,
    correctCount,
    unreachedCount,
    totalTarget,
    accuracyPct,
    performanceIndex,
    fluencyBonusPct,
    medianTimeMs,
    avgTimeSec,
    speedCategory: isAr ? speedCategory_ar : speedCategory_en,
    domainStats,
    strengths,
    growthAreas,
    radarSvg,
    summaryText
  };
}
